import { ArrowRightOutlined } from '@ant-design/icons';
import HeartLoader from '@/shared/ui/HeartLoader';
import type { SearchPatient, SearchStudy, SearchReport } from '@/models/search';
import { PatientResults } from './PatientResults';
import { StudyResults } from './StudyResults';
import { ReportResults } from './ReportResults';
import styles from '../GlobalSearch.module.css';

export const DropdownContent = ({
  isFetching,
  hasResults,
  data,
  debouncedQuery,
  handleSelect,
  totalResults,
  handleShowAll,
  highlightIndex = -1,
}: {
  isFetching: boolean;
  hasResults: boolean;
  data:
    | {
        patients: SearchPatient[];
        studies: SearchStudy[];
        reports: SearchReport[];
      }
    | undefined;
  debouncedQuery: string;
  handleSelect: (path: string) => void;
  totalResults: number;
  handleShowAll: () => void;
  highlightIndex?: number;
}) => {
  if (isFetching) {
    return (
      <div className={styles.loading}>
        <HeartLoader size="sm" showText text="Searching..." />
      </div>
    );
  }

  if (!hasResults) {
    return (
      <div className={styles.empty}>
        No results found for &ldquo;{debouncedQuery}&rdquo;
      </div>
    );
  }

  // Calculate offsets for each section so highlightIndex maps correctly
  const patientCount = Math.min(data?.patients.length ?? 0, 5);
  const studyCount = Math.min(data?.studies.length ?? 0, 5);

  return (
    <>
      {data && (
        <>
          <PatientResults
            patients={data.patients}
            onSelect={handleSelect}
            query={debouncedQuery}
            highlightIndex={highlightIndex}
            highlightOffset={0}
          />
          <StudyResults
            studies={data.studies}
            onSelect={handleSelect}
            query={debouncedQuery}
            highlightIndex={highlightIndex}
            highlightOffset={patientCount}
          />
          <ReportResults
            reports={data.reports}
            onSelect={handleSelect}
            query={debouncedQuery}
            highlightIndex={highlightIndex}
            highlightOffset={patientCount + studyCount}
          />
        </>
      )}
      <div
        className={styles.showAllRow}
        role="button"
        tabIndex={0}
        onClick={handleShowAll}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleShowAll();
        }}
      >
        <ArrowRightOutlined className={styles.showAllIcon} />
        <span>
          Show all {totalResults} result{totalResults !== 1 ? 's' : ''}
        </span>
      </div>
    </>
  );
};
